import * as React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from '@mui/material';
import Link from 'components/Link';
import Icon from 'components/Icon';
import theme from 'utils/theme';

import { getColor, globalTransitionDuration } from 'utils/theme';
import useEqualHeight from "hooks/useEqualHeight";

const ExpandableCard = props => {
	const { title, body, icon, link, expand, fullWidth, equalize, equalizeKey } = props;
	if (!link) console.warn(`ExpandableCard component requires a link prop. ${link} was provided instead.`);
	if (equalize && !equalizeKey) console.warn(`ExpandableCard component requires an equalizeKey prop. ${equalizeKey} was provided instead.`);

	const dataKey = `expandableCard_${equalizeKey}_`;
	
	const [cardBodyRef] = useEqualHeight(dataKey+'cardBody');
	const [cardTitleRef] = useEqualHeight(dataKey+'cardTitle');

	const cardWrapStyle = {
		height: '100%',
		'> a': {
			padding: '15px 20px 24px 23px',
			display: 'grid',
			backgroundColor: getColor('gray', 600),
			transition: `all ${globalTransitionDuration} ease-in-out`,
			borderRadius: '11px',
			textDecoration: 'none',
			height: '100%',
			gridColumnGap: '16px',
			[theme.breakpoints.up('sm')]: {
				padding: '40px',
				minHeight: '193px',
				...(expand ? {
					minHeight: '255px',
				} : {}),
				...(fullWidth ? {
					minHeight: '-webkit-fill-available',
				} : {})
			},
			'&:hover': {
				borderTopLeftRadius: '0',
				borderTopRightRadius: '2rem',
				borderBottomLeftRadius: '2rem',
				borderBottomRightRadius: '0',
				backgroundColor: getColor('blue', 500),
				...(fullWidth ? {
					backgroundColor: getColor('blue', 700),
					borderRadius: '0',
				} : {}),
			},
			...(expand ? {
				gridColumnGap: '0',
				gridRowGap: '6px'
			} : {}),
			...(fullWidth ? {
				height: '-webkit-fill-available',
				borderRadius: '0',
				borderBottom: `8px solid ${getColor('red', 500)}`,
				gridColumnGap: '0',
				gridRowGap: '20px' // Originally was 64px, changed per QA review and wanting less gap for the More Industries slider
			} : {}),
		},
		...(fullWidth ? {
			height: 'inherit',
		} : {}),
	};

	const titleWrapperStyle = {
		gridArea: '1 / 1 / 2 / 2',
		marginBottom: '15px',
		[theme.breakpoints.down('sm')]: {
			minHeight: 'auto !important',
		},
		[theme.breakpoints.up('sm')]: {
			marginBottom: '24px',
		},
		...(expand ? {
			display: 'flex',
			alignItems: 'center',
			marginBottom: 0,
		} : {}),
		...(fullWidth ? {
			gridArea: '1 / 1 / 2 / 2',
			marginBottom: 0,
			display: 'flex',
			alignItems: 'center',
		} : {})
	};

	const bodyWrapperStyle = {
		[theme.breakpoints.down('sm')]: {
			minHeight: 'auto !important',
		},
		gridArea: '2 / 1 / 3 / 2',
		...(fullWidth ? {
			gridArea: '2 / 1 / 3 / 3',
		} : {})
	};

	const imageWrapperStyle = {
		gridArea: '1 / 2 / 3 / 3',
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		'img': {
			maxHeight: '56px',
			minHeight: '56px',
			[theme.breakpoints.up('sm')]: {
				maxHeight: '99px',
				minHeight: '99px',
			},
		},
		...(expand ? {
			gridArea: '1 / 2 / 2 / 3',
			'img': {
				width: '44px',
				height: '44px',
				maxHeight: '44px',
				minHeight: '44px',
				[theme.breakpoints.up('sm')]: {
					width: '72px',
					height: '72px',
				},
			}
		} : {}),
		...(fullWidth ? {
			gridArea: '2 / 1 / 1 / 3',
			'img': {
				width: '44px',
				height: '44px',
				maxHeight: '44px',
				minHeight: '44px',
				[theme.breakpoints.up('sm')]: {
					width: '72px',
					height: '72px',
				},
			}
		} : {})
	};

	return (
		<Box sx={cardWrapStyle} component={'div'}>
			<Link title={title} to={link}>
				<Box ref={cardTitleRef} data-equal-height={equalize ? dataKey+"cardTitle" : null}  sx={titleWrapperStyle}><Typography component="h2" variant={'h2CardTitle'}>{title}</Typography></Box>
				<Box ref={cardBodyRef} data-equal-height={equalize ? dataKey+"cardBody" : null} sx={bodyWrapperStyle}><Typography variant={'body1MastheadSmall'}>{body}</Typography></Box>
				{icon && <Box sx={imageWrapperStyle}><Icon width={"99"} height={"100%"} symbol={icon} /></Box>}
			</Link>
		</Box>
	)
};

ExpandableCard.propTypes = {
    title: PropTypes.string.isRequired,
    body: PropTypes.node.isRequired,
    icon: PropTypes.string,
    link: PropTypes.string.isRequired,
    expand: PropTypes.bool,
    fullWidth: PropTypes.bool,
	equalize: PropTypes.bool,
	equalizeKey: PropTypes.string,
};

ExpandableCard.defaultProps = {
    expand: false,
    fullWidth: false,
    icon: "",
	equalize: true,
	equalizeKey: "",
};

export default ExpandableCard;